/**
 * Canonical client-portal permission registry and compatibility evaluator.
 *
 * Client assignments store this document in client_users.permissions. Version 2
 * adds explicit grants while retaining the legacy fields during a deploy-safe
 * migration. Every client-facing authorization check should use a key from this
 * registry rather than introducing a new hidden_views/hidden_providers string.
 */

import {
  publishingApprovalsPath,
  publishingCalendarPath,
  publishingContentPath,
} from "@/lib/publishing/paths";

export const CLIENT_PERMISSION_PRESET_KEYS = [
  "viewer",
  "reviewer",
  "collaborator",
  "client_admin",
  "publishing_approver",
  "custom",
] as const;

export type ClientPermissionPreset = (typeof CLIENT_PERMISSION_PRESET_KEYS)[number];

export const CLIENT_PERMISSION_PRESETS: Record<
  Exclude<ClientPermissionPreset, "custom">,
  { label: string; description: string }
> = {
  viewer: {
    label: "Viewer",
    description: "Read dashboards, connected data, reports, and downloads.",
  },
  reviewer: {
    label: "Reviewer",
    description: "Viewer access plus comments and assigned approval actions.",
  },
  collaborator: {
    label: "Collaborator",
    description: "Reviewer access plus custom dashboard creation.",
  },
  client_admin: {
    label: "Client Admin",
    description: "Broad client access, including AI exports; paid and agency operations remain staff-only.",
  },
  publishing_approver: {
    label: "Publishing Approver",
    description: "Publishing only: calendar, posts, sitemap content, comments, and assigned approvals. Lands on the Publishing calendar.",
  },
};

export type PermissionCategory =
  | "Dashboard Views"
  | "Company Features"
  | "Integrations"
  | "Tools & Audits"
  | "Client Actions"
  | "High-Risk Actions";

type LegacyPermissionMapping =
  | { kind: "hidden_view"; key: string }
  | { kind: "hidden_provider"; key: string }
  | { kind: "boolean"; key: "can_view_insights" | "can_create_dashboards" };

interface ClientPermissionDefinition {
  label: string;
  description: string;
  category: PermissionCategory;
  default: boolean;
  legacy?: LegacyPermissionMapping;
  presets: readonly Exclude<ClientPermissionPreset, "custom">[];
}

const ALL_READ_PRESETS = ["viewer", "reviewer", "collaborator", "client_admin"] as const;
const REVIEW_PRESETS = ["reviewer", "collaborator", "client_admin"] as const;
const COLLABORATOR_PRESETS = ["collaborator", "client_admin"] as const;
// Publishing Approver is deliberately narrow: it joins ONLY the publishing
// read/review keys below (social posts + sitemap content), so every
// dashboard, provider, and analytics grant stays off.
const PUBLISHING_READ_PRESETS = [...ALL_READ_PRESETS, "publishing_approver"] as const;
const PUBLISHING_REVIEW_PRESETS = [...REVIEW_PRESETS, "publishing_approver"] as const;
// Execution, paid API, credential, and destructive grants are never enabled
// implicitly by a preset. An administrator must opt into each one.
const EXPLICIT_ONLY_PRESETS = [] as const;

export const CLIENT_PERMISSION_DEFINITIONS = {
  "dashboard.overview.view": {
    label: "Overview dashboard",
    description: "View the company overview dashboard.",
    category: "Dashboard Views",
    default: true,
    legacy: { kind: "hidden_view", key: "overview" },
    presets: ALL_READ_PRESETS,
  },
  "dashboard.performance.view": {
    label: "Performance dashboard",
    description: "View cross-channel performance analytics.",
    category: "Dashboard Views",
    default: true,
    legacy: { kind: "hidden_view", key: "performance" },
    presets: ALL_READ_PRESETS,
  },
  "dashboard.acquisition.view": {
    label: "Acquisition dashboard",
    description: "View traffic acquisition analytics.",
    category: "Dashboard Views",
    default: true,
    legacy: { kind: "hidden_view", key: "acquisition" },
    presets: ALL_READ_PRESETS,
  },
  "dashboard.engagement.view": {
    label: "Engagement dashboard",
    description: "View engagement and conversion analytics.",
    category: "Dashboard Views",
    default: true,
    legacy: { kind: "hidden_view", key: "engagement" },
    presets: ALL_READ_PRESETS,
  },
  "dashboard.ai.view": {
    label: "AI Analytics dashboard",
    description: "View AI-search and AI-referral analytics.",
    category: "Dashboard Views",
    default: true,
    legacy: { kind: "hidden_view", key: "ai" },
    presets: ALL_READ_PRESETS,
  },
  "dashboard.health.view": {
    label: "Health dashboard",
    description: "View technical, SEO, accessibility, and uptime summaries.",
    category: "Dashboard Views",
    default: true,
    legacy: { kind: "hidden_view", key: "health" },
    presets: ALL_READ_PRESETS,
  },

  "pulse.view": {
    label: "Pulse",
    description: "View the company Pulse and change summaries.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "pulse" },
    presets: ALL_READ_PRESETS,
  },
  "campaigns.view": {
    label: "Campaigns",
    description: "View campaigns, goals, tracked pages, ads, keywords, and tasks.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "campaign_tracker" },
    presets: ALL_READ_PRESETS,
  },
  "ai_recommendations.view": {
    label: "AI Recommendations",
    description: "View stored AI recommendations in Pulse.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "ai_recommendations" },
    presets: ALL_READ_PRESETS,
  },
  "reports.view": {
    label: "Reports",
    description: "View reports marked visible to client accounts.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "reports" },
    presets: ALL_READ_PRESETS,
  },
  "custom_dashboards.view": {
    label: "Custom dashboards",
    description: "View the company's custom dashboards.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "custom_dashboards" },
    presets: ALL_READ_PRESETS,
  },
  "social.view": {
    label: "Social Publishing",
    description: "View social posts, calendar, gallery, and assigned reviews.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "social_publishing" },
    presets: PUBLISHING_READ_PRESETS,
  },
  "sitemaps.view": {
    label: "Sitemaps",
    description: "View sitemap structures and content plans.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "sitemap_planner" },
    presets: PUBLISHING_READ_PRESETS,
  },
  "publishing.content.view": {
    label: "Publishing Content list",
    description: "View the company-wide list of scheduled posts and pages.",
    category: "Company Features",
    default: true,
    // No legacy mapping: the key is new, so there is no hidden_views mirror.
    presets: PUBLISHING_READ_PRESETS,
  },
  "media.view": {
    label: "Media Library",
    description: "View company images, videos, and their usage.",
    category: "Company Features",
    default: true,
    legacy: { kind: "hidden_view", key: "media_library" },
    presets: ALL_READ_PRESETS,
  },
  "site_vitals.view": {
    label: "Site Vitals",
    description: "View hosting and operational health analytics.",
    category: "Company Features",
    default: true,
    presets: ALL_READ_PRESETS,
  },

  "providers.ga4.view": {
    label: "Google Analytics",
    description: "View Google Analytics data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "ga4" },
    presets: ALL_READ_PRESETS,
  },
  "providers.gsc.view": {
    label: "Search Console",
    description: "View Google Search Console data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "gsc" },
    presets: ALL_READ_PRESETS,
  },
  "providers.google_ads.view": {
    label: "Google Ads",
    description: "View Google Ads data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "google_ads" },
    presets: ALL_READ_PRESETS,
  },
  "providers.meta_ads.view": {
    label: "Meta Ads",
    description: "View Meta Ads data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "meta_ads" },
    presets: ALL_READ_PRESETS,
  },
  "providers.stackadapt.view": {
    label: "StackAdapt",
    description: "View StackAdapt data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "stackadapt" },
    presets: ALL_READ_PRESETS,
  },
  "providers.callrail.view": {
    label: "CallRail",
    description: "View calls, forms, and permitted recordings.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "callrail" },
    presets: ALL_READ_PRESETS,
  },
  "providers.hubspot.view": {
    label: "HubSpot Leads",
    description: "View CRM lead, lifecycle funnel, and deal data.",
    category: "Integrations",
    default: true,
    presets: ALL_READ_PRESETS,
  },
  "providers.hubspot_email.view": {
    label: "HubSpot Email",
    description: "View marketing email campaign and send metrics.",
    category: "Integrations",
    default: true,
    presets: ALL_READ_PRESETS,
  },
  "providers.gbp.view": {
    label: "Google Business Profile",
    description: "View Google Business Profile data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "gbp" },
    presets: ALL_READ_PRESETS,
  },
  "providers.meta.view": {
    label: "Meta",
    description: "View Facebook Page analytics.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "meta" },
    presets: ALL_READ_PRESETS,
  },
  "providers.instagram.view": {
    label: "Instagram",
    description: "View Instagram analytics.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "instagram" },
    presets: ALL_READ_PRESETS,
  },
  "providers.linkedin.view": {
    label: "LinkedIn",
    description: "View LinkedIn analytics.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "linkedin" },
    presets: ALL_READ_PRESETS,
  },
  "providers.wordpress.view": {
    label: "WordPress",
    description: "View WordPress site data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "wordpress" },
    presets: ALL_READ_PRESETS,
  },
  "providers.joomla.view": {
    label: "Joomla",
    description: "View Joomla site data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "joomla" },
    presets: ALL_READ_PRESETS,
  },
  "providers.cloudways.view": {
    label: "Cloudways",
    description: "View Cloudways-backed site data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "cloudways" },
    presets: ALL_READ_PRESETS,
  },
  "providers.dataforseo.view": {
    label: "SEO Research",
    description: "View stored DataForSEO research and competitor data.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "dataforseo" },
    presets: ALL_READ_PRESETS,
  },
  "providers.competitor_profiles.view": {
    label: "Competitor Profiles",
    description: "View stored competitor profiles and comparisons.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "competitor_profiles" },
    presets: ALL_READ_PRESETS,
  },
  "providers.aeo_research.view": {
    label: "AEO Research",
    description: "View AI answer-engine visibility, tracked prompts, and transcripts.",
    category: "Integrations",
    default: true,
    legacy: { kind: "hidden_provider", key: "aeo_research" },
    presets: ALL_READ_PRESETS,
  },

  "providers.pagespeed.view": {
    label: "PageSpeed Insights",
    description: "View stored performance audits.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "pagespeed" },
    presets: ALL_READ_PRESETS,
  },
  "providers.site_audit.view": {
    label: "Security & Tech Audit",
    description: "View stored security and technology audits.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "site_audit" },
    presets: ALL_READ_PRESETS,
  },
  "providers.link_checker.view": {
    label: "Link Checker",
    description: "View stored broken-link results.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "link_checker" },
    presets: ALL_READ_PRESETS,
  },
  "providers.seo_crawl.view": {
    label: "SEO Audit",
    description: "View stored SEO crawl results.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "seo_crawl" },
    presets: ALL_READ_PRESETS,
  },
  "providers.ai_search_health.view": {
    label: "AI Search Health",
    description: "View AI-search visibility results independently of the SEO crawl.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "ai_search_health" },
    presets: ALL_READ_PRESETS,
  },
  "providers.accessibility.view": {
    label: "Accessibility Audit",
    description: "View stored accessibility scans and issue detail.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "accessibility" },
    presets: ALL_READ_PRESETS,
  },
  "providers.uptime_builtin.view": {
    label: "Uptime Monitor",
    description: "View uptime checks, incidents, and SSL status.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "uptime_builtin" },
    presets: ALL_READ_PRESETS,
  },
  "providers.keyword_tracker.view": {
    label: "Keyword Tracker",
    description: "View tracked keywords, ranks, pages, and trends.",
    category: "Tools & Audits",
    default: true,
    legacy: { kind: "hidden_provider", key: "keyword_tracker" },
    presets: ALL_READ_PRESETS,
  },

  "insights.view": {
    label: "View Insights",
    description: "Open automated Insights on dashboards.",
    category: "Client Actions",
    default: true,
    legacy: { kind: "boolean", key: "can_view_insights" },
    presets: ALL_READ_PRESETS,
  },
  "custom_dashboards.create": {
    label: "Create custom dashboards",
    description: "Create company-wide custom dashboard tabs.",
    category: "Client Actions",
    default: false,
    legacy: { kind: "boolean", key: "can_create_dashboards" },
    presets: COLLABORATOR_PRESETS,
  },
  "custom_dashboards.manage": {
    label: "Manage custom dashboards",
    description: "Edit and delete company-wide custom dashboard tabs.",
    category: "Client Actions",
    default: false,
    presets: COLLABORATOR_PRESETS,
  },
  "exports.print_download": {
    label: "Print and download",
    description: "Open printable dashboard, audit, report, and sitemap exports.",
    category: "Client Actions",
    default: true,
    presets: ALL_READ_PRESETS,
  },
  "exports.ai": {
    label: "Export for AI",
    description: "Export permitted analytics sections as AI-ready Markdown.",
    category: "Client Actions",
    default: false,
    presets: ["client_admin"],
  },
  "social.comment": {
    label: "Comment on social posts",
    description: "Add comments to accessible social posts.",
    category: "Client Actions",
    default: true,
    presets: PUBLISHING_REVIEW_PRESETS,
  },
  "social.approve": {
    label: "Approve assigned social posts",
    description: "Approve or request revisions when assigned in the active workflow round.",
    category: "Client Actions",
    default: true,
    presets: PUBLISHING_REVIEW_PRESETS,
  },
  "social.create_drafts": {
    label: "Create social drafts",
    description: "Create and duplicate draft posts using existing connected channels and media.",
    category: "Client Actions",
    default: false,
    presets: COLLABORATOR_PRESETS,
  },
  "social.edit_drafts": {
    label: "Edit social drafts",
    description: "Edit draft or revision-requested social posts without scheduling or publishing them.",
    category: "Client Actions",
    default: false,
    presets: COLLABORATOR_PRESETS,
  },
  "sitemaps.comment": {
    label: "Comment on sitemap content",
    description: "Add comments to accessible sitemap pages.",
    category: "Client Actions",
    default: true,
    presets: PUBLISHING_REVIEW_PRESETS,
  },
  "sitemaps.approve": {
    label: "Approve assigned sitemap content",
    description: "Approve or request revisions when assigned in the active workflow round.",
    category: "Client Actions",
    default: true,
    presets: PUBLISHING_REVIEW_PRESETS,
  },
  "sitemaps.export": {
    label: "Export sitemaps",
    description: "Download permitted sitemap versions as CSV.",
    category: "Client Actions",
    default: true,
    presets: ALL_READ_PRESETS,
  },
  "sitemaps.edit_content": {
    label: "Edit sitemap content drafts",
    description: "Manually edit page metadata, section copy, and existing image details outside active review.",
    category: "Client Actions",
    default: false,
    presets: COLLABORATOR_PRESETS,
  },

  "campaigns.create": {
    label: "Create campaigns",
    description: "Create campaign projects for this company.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "campaigns.manage": {
    label: "Manage campaigns and goals",
    description: "Edit campaign configuration, goals, tracked pages, ads, and linked keywords.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "campaigns.delete": {
    label: "Delete campaigns",
    description: "Permanently delete campaigns, goals, links, tasks, and snapshots.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "campaigns.tasks.view": {
    label: "View campaign tasks",
    description: "View internal campaign tasks and stored task analysis.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "campaigns.tasks.manage": {
    label: "Manage campaign tasks",
    description: "Create, edit, complete, and delete campaign tasks without running AI analysis.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "campaigns.refresh_data": {
    label: "Refresh campaign data",
    description: "Force campaign provider reads, keyword backfills, baselines, and snapshots.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.submit_review": {
    label: "Submit Social reviews",
    description: "Submit or resubmit Social drafts into an approval workflow.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.schedule": {
    label: "Schedule Social posts",
    description: "Schedule or unschedule approved Social targets.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.publish": {
    label: "Publish Social posts",
    description: "Publish approved Social targets immediately or confirm a manual publish.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.upload_media": {
    label: "Upload Social media",
    description: "Upload new images and videos while composing Social drafts.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.cancel": {
    label: "Cancel Social posts",
    description: "Remove submitted or approved posts from approval and scheduling.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.delete": {
    label: "Delete Social posts",
    description: "Permanently delete Social posts and their targets.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "social.manage_workflows": {
    label: "Manage Social workflows",
    description: "Create, update, and delete Social approval workflows.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.create": {
    label: "Create Sitemaps",
    description: "Create blank Sitemap projects for this company.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.manage_versions": {
    label: "Manage Sitemap versions",
    description: "Create, duplicate, rename, lock, unlock, and delete Sitemap versions.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.edit_structure": {
    label: "Edit Sitemap structure",
    description: "Add, update, move, duplicate, and delete Sitemap pages and sections.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.generate_ai": {
    label: "Run Sitemap AI",
    description: "Run paid AI generation for pages, content, metadata, full Sitemaps, and variations.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.import": {
    label: "Import Sitemap structure",
    description: "Build a Sitemap version from stored crawl data or a new live crawl.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.manage_images": {
    label: "Manage Sitemap images",
    description: "Upload, attach, and remove images in Sitemap content plans.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.delete": {
    label: "Delete Sitemaps",
    description: "Permanently delete Sitemap projects and every version they contain.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.manage_workflows": {
    label: "Manage Sitemap workflows",
    description: "Create, update, and delete Sitemap content approval workflows.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "sitemaps.submit_review": {
    label: "Submit Sitemap reviews",
    description: "Submit, resubmit, and renotify Sitemap content approval workflows.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "providers.site_audit.run": {
    label: "Run Security & Tech Audits",
    description: "Start paid or resource-intensive Security & Tech Audit jobs.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "providers.seo_crawl.run": {
    label: "Run SEO Crawls",
    description: "Start configurable SEO crawl jobs.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "providers.ai_search_health.run": {
    label: "Run AI Search Health",
    description: "Start AI Search Health audit jobs.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "providers.accessibility.run": {
    label: "Run Accessibility Audits",
    description: "Start configurable accessibility scan jobs.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "providers.pagespeed.run": {
    label: "Run PageSpeed Audits",
    description: "Start configurable PageSpeed audit jobs that consume API quota.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "providers.keyword_tracker.run": {
    label: "Run Keyword Tracker jobs",
    description: "Fetch keyword ranks and run page-history backfills.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "ai_recommendations.run": {
    label: "Generate AI Recommendations",
    description: "Run paid AI Recommendation generation from Pulse.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
  "integrations.manage_connections": {
    label: "Manage integration connections",
    description: "Add, update, authorize, and disconnect company data-source credentials.",
    category: "High-Risk Actions",
    default: false,
    presets: EXPLICIT_ONLY_PRESETS,
  },
} as const satisfies Record<string, ClientPermissionDefinition>;

export type ClientPermissionKey = keyof typeof CLIENT_PERMISSION_DEFINITIONS;
export type ClientPermissionGrants = Partial<Record<ClientPermissionKey, boolean>>;

const CLIENT_DATA_PROVIDER_PERMISSIONS: Record<string, ClientPermissionKey> = {
  ga4: "providers.ga4.view",
  gsc: "providers.gsc.view",
  google_ads: "providers.google_ads.view",
  meta_ads: "providers.meta_ads.view",
  stackadapt: "providers.stackadapt.view",
  callrail: "providers.callrail.view",
  hubspot: "providers.hubspot.view",
  hubspot_email: "providers.hubspot_email.view",
  gbp: "providers.gbp.view",
  meta: "providers.meta.view",
  social_meta: "providers.meta.view",
  meta_social: "providers.meta.view",
  instagram: "providers.instagram.view",
  social_instagram: "providers.instagram.view",
  instagram_social: "providers.instagram.view",
  linkedin: "providers.linkedin.view",
  social_linkedin: "providers.linkedin.view",
  linkedin_social: "providers.linkedin.view",
  wordpress: "providers.wordpress.view",
  joomla: "providers.joomla.view",
  cloudways: "providers.cloudways.view",
  dataforseo: "providers.dataforseo.view",
  seo_research: "providers.dataforseo.view",
  reputation: "providers.dataforseo.view",
  competitor_profiles: "providers.competitor_profiles.view",
  aeo_research: "providers.aeo_research.view",
  ai_visibility: "providers.dataforseo.view",
  pagespeed: "providers.pagespeed.view",
  cwv: "providers.pagespeed.view",
  site_audit: "providers.site_audit.view",
  link_checker: "providers.link_checker.view",
  link_check: "providers.link_checker.view",
  seo_crawl: "providers.seo_crawl.view",
  ai_search_health: "providers.ai_search_health.view",
  accessibility: "providers.accessibility.view",
  uptime_builtin: "providers.uptime_builtin.view",
  uptime: "providers.uptime_builtin.view",
  keyword_tracker: "providers.keyword_tracker.view",
  keyword: "providers.keyword_tracker.view",
  site_vitals: "site_vitals.view",
};

/**
 * Landing pages a client user can be sent to when they open a company.
 * Stored per assignment as permissions.landing_page; unset means "first
 * available page" via CLIENT_LANDING_FALLBACK_ORDER.
 */
export const CLIENT_LANDING_PAGE_KEYS = [
  "pulse",
  "dashboard",
  "campaigns",
  "publishing_calendar",
  "publishing_approvals",
  "publishing_content",
] as const;

export type ClientLandingPageKey = (typeof CLIENT_LANDING_PAGE_KEYS)[number];

export const CLIENT_LANDING_PAGES: Record<ClientLandingPageKey, { label: string }> = {
  pulse: { label: "Pulse" },
  dashboard: { label: "Overview dashboard" },
  campaigns: { label: "Campaigns" },
  publishing_calendar: { label: "Publishing calendar" },
  publishing_approvals: { label: "Publishing approvals" },
  publishing_content: { label: "Publishing content list" },
};

const LANDING_PAGE_KEY_SET = new Set<string>(CLIENT_LANDING_PAGE_KEYS);

/**
 * When no explicit landing page is stored (or the stored one is no longer
 * permitted), the first allowed entry here wins. Pulse leads so existing
 * accounts keep landing exactly where they do today.
 */
export const CLIENT_LANDING_FALLBACK_ORDER: readonly ClientLandingPageKey[] = [
  "pulse",
  "dashboard",
  "publishing_calendar",
  "campaigns",
];

/** Per-company module toggles a landing decision needs beyond the grants. */
export interface ClientLandingModules {
  socialEnabled: boolean;
  sitemapsEnabled: boolean;
}

export function clientLandingPageAllowed(
  key: ClientLandingPageKey,
  permissions: EffectiveClientUserPermissions,
  modules: ClientLandingModules,
): boolean {
  const grants = permissions.grants;
  const publishingAllowed =
    (modules.socialEnabled && grants["social.view"]) ||
    (modules.sitemapsEnabled && grants["sitemaps.view"]);
  switch (key) {
    case "pulse":
      return grants["pulse.view"];
    case "dashboard":
      return grants["dashboard.overview.view"];
    case "campaigns":
      return grants["campaigns.view"];
    case "publishing_calendar":
    case "publishing_approvals":
      return publishingAllowed;
    case "publishing_content":
      return publishingAllowed && grants["publishing.content.view"];
  }
}

/**
 * Pick the landing page for a client user in one company: their explicit
 * choice when it is still permitted, otherwise the first permitted fallback.
 * Null means nothing in the fallback list is viewable.
 */
export function resolveClientLandingPage(
  permissions: EffectiveClientUserPermissions,
  modules: ClientLandingModules,
): ClientLandingPageKey | null {
  const chosen = permissions.landing_page;
  if (chosen && clientLandingPageAllowed(chosen, permissions, modules)) return chosen;
  for (const key of CLIENT_LANDING_FALLBACK_ORDER) {
    if (clientLandingPageAllowed(key, permissions, modules)) return key;
  }
  return null;
}

export function clientLandingHref(key: ClientLandingPageKey, clientId: string): string {
  switch (key) {
    case "pulse":
      return `/clients/${clientId}/pulse`;
    case "dashboard":
      return `/clients/${clientId}/dashboard/overview`;
    case "campaigns":
      return `/clients/${clientId}/campaigns`;
    case "publishing_calendar":
      return publishingCalendarPath(clientId);
    case "publishing_approvals":
      return publishingApprovalsPath(clientId);
    case "publishing_content":
      return publishingContentPath(clientId);
  }
}

/**
 * Landing page a preset carries with it. Applying a preset overwrites the
 * stored landing so a template is one click: Publishing Approver lands on
 * the calendar, every other preset returns to the default resolution.
 */
const PRESET_DEFAULT_LANDING: Partial<
  Record<Exclude<ClientPermissionPreset, "custom">, ClientLandingPageKey>
> = {
  publishing_approver: "publishing_calendar",
};

/** Raw JSON document stored in client_users.permissions. */
export interface ClientUserPermissions {
  version?: 2;
  preset?: ClientPermissionPreset;
  // null and undefined both mean "default landing" (EffectiveClientUserPermissions
  // reports null explicitly, and must stay assignable to this raw shape).
  landing_page?: ClientLandingPageKey | null;
  grants?: ClientPermissionGrants;
  hidden_providers?: string[];
  hidden_views?: string[];
  can_view_insights?: boolean;
  can_create_dashboards?: boolean;
}

/** Fully evaluated document returned by getClientUserPermissions. */
export interface EffectiveClientUserPermissions {
  version: 2;
  preset: ClientPermissionPreset;
  landing_page: ClientLandingPageKey | null;
  grants: Record<ClientPermissionKey, boolean>;
  hidden_providers: string[];
  hidden_views: string[];
  can_view_insights: boolean;
  can_create_dashboards: boolean;
}

/** Resolve live, widget, insight, and report aliases through one capability map. */
export function clientCanViewDataProvider(
  permissions: EffectiveClientUserPermissions,
  provider: string,
): boolean {
  if (provider === "site_vitals") {
    return permissions.grants["site_vitals.view"] && permissions.grants["providers.cloudways.view"];
  }
  const permission = CLIENT_DATA_PROVIDER_PERMISSIONS[provider];
  return permission ? permissions.grants[permission] : false;
}

const PERMISSION_KEYS = Object.keys(CLIENT_PERMISSION_DEFINITIONS) as ClientPermissionKey[];
const PERMISSION_KEY_SET = new Set<string>(PERMISSION_KEYS);
const PRESET_KEY_SET = new Set<string>(CLIENT_PERMISSION_PRESET_KEYS);

export const CLIENT_PERMISSION_CATEGORIES: PermissionCategory[] = [
  "Dashboard Views",
  "Company Features",
  "Integrations",
  "Tools & Audits",
  "Client Actions",
  "High-Risk Actions",
];

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === "string"))]
    : [];
}

function parsePermissionObject(value: unknown): Record<string, unknown> {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Strip unknown keys and invalid values before a permissions document is persisted. */
export function sanitizeClientUserPermissions(value: unknown): ClientUserPermissions {
  const raw = parsePermissionObject(value);
  const rawGrants = parsePermissionObject(raw.grants);
  const grants: ClientPermissionGrants = {};
  for (const [key, enabled] of Object.entries(rawGrants)) {
    if (PERMISSION_KEY_SET.has(key) && typeof enabled === "boolean") {
      grants[key as ClientPermissionKey] = enabled;
    }
  }

  const preset = typeof raw.preset === "string" && PRESET_KEY_SET.has(raw.preset)
    ? (raw.preset as ClientPermissionPreset)
    : undefined;

  const landingPage = typeof raw.landing_page === "string" && LANDING_PAGE_KEY_SET.has(raw.landing_page)
    ? (raw.landing_page as ClientLandingPageKey)
    : undefined;

  return {
    version: 2,
    preset: preset ?? "custom",
    ...(landingPage ? { landing_page: landingPage } : {}),
    ...(Object.keys(grants).length > 0 ? { grants } : {}),
    hidden_providers: stringArray(raw.hidden_providers),
    hidden_views: stringArray(raw.hidden_views),
    ...(typeof raw.can_view_insights === "boolean" ? { can_view_insights: raw.can_view_insights } : {}),
    ...(typeof raw.can_create_dashboards === "boolean" ? { can_create_dashboards: raw.can_create_dashboards } : {}),
  };
}

function legacyPermissionValue(
  raw: ClientUserPermissions,
  definition: ClientPermissionDefinition,
): boolean {
  const mapping = definition.legacy;
  if (!mapping) return definition.default;
  if (mapping.kind === "hidden_view") {
    return !(raw.hidden_views ?? []).includes(mapping.key);
  }
  if (mapping.kind === "hidden_provider") {
    return !(raw.hidden_providers ?? []).includes(mapping.key);
  }
  const value = raw[mapping.key];
  return typeof value === "boolean" ? value : definition.default;
}

export function permissionValue(value: unknown, key: ClientPermissionKey): boolean {
  const raw = sanitizeClientUserPermissions(value);
  const explicit = raw.grants?.[key];
  if (typeof explicit === "boolean") return explicit;

  // Site Vitals historically shared the Cloudways provider toggle. Keep that
  // fallback without giving two v2 permissions ownership of the same legacy
  // hidden-provider key (which would make evaluation order significant).
  if (key === "site_vitals.view") {
    return !(raw.hidden_providers ?? []).includes("cloudways");
  }

  return legacyPermissionValue(raw, CLIENT_PERMISSION_DEFINITIONS[key]);
}

export function evaluateClientUserPermissions(value: unknown): EffectiveClientUserPermissions {
  const raw = sanitizeClientUserPermissions(value);
  const hiddenViews = new Set(raw.hidden_views ?? []);
  const hiddenProviders = new Set(raw.hidden_providers ?? []);
  const grants = {} as Record<ClientPermissionKey, boolean>;

  for (const key of PERMISSION_KEYS) {
    const definition: ClientPermissionDefinition = CLIENT_PERMISSION_DEFINITIONS[key];
    const enabled = permissionValue(raw, key);
    grants[key] = enabled;
    const mapping = definition.legacy;
    if (mapping?.kind === "hidden_view") {
      if (enabled) hiddenViews.delete(mapping.key);
      else hiddenViews.add(mapping.key);
    } else if (mapping?.kind === "hidden_provider") {
      if (enabled) hiddenProviders.delete(mapping.key);
      else hiddenProviders.add(mapping.key);
    }
  }

  return {
    version: 2,
    preset: raw.preset ?? "custom",
    landing_page: raw.landing_page ?? null,
    grants,
    hidden_providers: [...hiddenProviders],
    hidden_views: [...hiddenViews],
    can_view_insights: grants["insights.view"],
    can_create_dashboards: grants["custom_dashboards.create"],
  };
}

/**
 * Produce the canonical persisted document, including legacy mirror fields
 * still consumed by database RLS policies during the compatibility window.
 */
export function normalizeClientUserPermissionsForStorage(value: unknown): ClientUserPermissions {
  const raw = sanitizeClientUserPermissions(value);
  const effective = evaluateClientUserPermissions(raw);
  return {
    ...raw,
    version: 2,
    preset: raw.preset ?? "custom",
    hidden_providers: effective.hidden_providers,
    hidden_views: effective.hidden_views,
    can_view_insights: effective.can_view_insights,
    can_create_dashboards: effective.can_create_dashboards,
  };
}

export function setClientPermissionGrant(
  value: unknown,
  key: ClientPermissionKey,
  enabled: boolean,
): ClientUserPermissions {
  const raw = sanitizeClientUserPermissions(value);
  return normalizeClientUserPermissionsForStorage({
    ...raw,
    version: 2,
    preset: "custom",
    grants: { ...raw.grants, [key]: enabled },
  });
}

export function applyClientPermissionPreset(
  value: unknown,
  preset: Exclude<ClientPermissionPreset, "custom">,
): ClientUserPermissions {
  const raw = sanitizeClientUserPermissions(value);
  const grants = {} as Record<ClientPermissionKey, boolean>;
  for (const key of PERMISSION_KEYS) {
    const definition: ClientPermissionDefinition = CLIENT_PERMISSION_DEFINITIONS[key];
    grants[key] = definition.presets.includes(preset);
  }
  // A preset is a template, so it owns the landing page too: presets without
  // a default landing clear any stored choice back to default resolution.
  const { landing_page: _discarded, ...rest } = raw;
  const landing = PRESET_DEFAULT_LANDING[preset];
  return normalizeClientUserPermissionsForStorage({
    ...rest,
    version: 2,
    preset,
    grants,
    ...(landing ? { landing_page: landing } : {}),
  });
}

/**
 * Set or clear the stored landing page. Unlike grant edits this does NOT flip
 * the preset to custom: where someone lands is orthogonal to what they may see.
 */
export function setClientLandingPage(
  value: unknown,
  landingPage: ClientLandingPageKey | null,
): ClientUserPermissions {
  const raw = sanitizeClientUserPermissions(value);
  const { landing_page: _discarded, ...rest } = raw;
  return normalizeClientUserPermissionsForStorage({
    ...rest,
    version: 2,
    ...(landingPage ? { landing_page: landingPage } : {}),
  });
}

export function isClientPermissionKey(value: string): value is ClientPermissionKey {
  return PERMISSION_KEY_SET.has(value);
}
